/**
 * ENG-5389: scrape Claude Code's "X% of your weekly limit · resets <date>"
 * banner from each Claude Code agent's pane.log and forward to the API as
 * an observation. Wired into the per-agent processing loop in
 * manager-worker.ts; only Claude Code framework agents are checked.
 *
 * "Only on change" semantics:
 *   - Keep an in-memory last-observed value per code_name (pct + reset date).
 *   - Skip the POST when the next banner read matches it.
 *   - This trades a tiny startup-blind window (we don't POST the first
 *     observation after restart if it matches what the server already has)
 *     for a major drop in write volume. The alert path (the API-side
 *     usage-limit-monitor cron, ENG-6183) reads the latest observation per
 *     agent each tick and re-derives the at-limit / approaching state from it,
 *     so the missing duplicate write doesn't change the answer.
 *
 * Per-agent file read is rate-limited to MIN_CHECK_INTERVAL_MS so the
 * manager's normal ~10-30s poll doesn't translate into N file reads/sec for
 * large hosts.
 */

import { parseUsageBanner } from '@augmented/core';
import { readPaneLogTail } from './persistent-session.js';

const MIN_CHECK_INTERVAL_MS = 60_000;
// ENG-6284: the weekly-limit banner renders only intermittently in the Claude
// Code pane, and a busy agent's pane churns fast (synthetic probes, spinner
// redraws), so a small window almost never contains the banner at sample time —
// the scrape silently produced zero observations and the alert path was starved
// of input (an agent went to 100% with no alert). A wide window reliably catches
// a recent banner. This is cheap: `readPaneLogTail` already reads the whole
// pane.log into memory regardless of the line count — the param only bounds the
// returned slice — so widening it adds no I/O. `parseUsageBanner` returns the
// most-recent banner in the slice, so a window spanning the week's climb still
// reports current usage, not a stale lower pct.
const PANE_TAIL_LINES_FOR_BANNER = 5000;

interface MonitorState {
  /** Last value successfully POSTed to the API (or read at startup). */
  lastPct: number | null;
  /** ISO of the lastPct's week_resets_at. */
  lastWeekResetsAt: string | null;
  /** Last time we actually opened pane.log for this code_name. */
  lastCheckedAt: number;
}

const state = new Map<string, MonitorState>();

/**
 * Test helper — reset module state. Not for production use.
 */
export function _resetUsageBannerMonitorState(): void {
  state.clear();
}

/**
 * Minimal API surface the monitor needs. Lets the unit test pass a stub
 * without dragging the real api-client transport in.
 */
export interface UsageBannerMonitorApi {
  post(path: string, body: unknown): Promise<unknown>;
}

/**
 * Read this agent's pane.log, parse the weekly-usage banner, and POST an
 * observation if (and only if) it differs from the last value we saw.
 *
 * Errors are swallowed and reported to `log`. This is a soft observability
 * path; an error here must not break the manager poll cycle.
 */
export async function maybeReportUsageBanner(args: {
  api: UsageBannerMonitorApi;
  codeName: string;
  agentId: string;
  log: (msg: string) => void;
  now?: Date;
}): Promise<void> {
  const { api, codeName, agentId, log } = args;
  const now = args.now ?? new Date();
  const nowMs = now.getTime();

  const existing = state.get(codeName);
  if (existing && nowMs - existing.lastCheckedAt < MIN_CHECK_INTERVAL_MS) {
    return;
  }

  let tail: string | null;
  try {
    tail = readPaneLogTail(codeName, PANE_TAIL_LINES_FOR_BANNER);
  } catch (err) {
    log(`[usage-banner] readPaneLogTail failed for '${codeName}': ${(err as Error).message}`);
    return;
  }

  // Always record the check timestamp so a missing banner doesn't make us
  // re-read the file on every tick.
  const next: MonitorState = {
    lastPct: existing?.lastPct ?? null,
    lastWeekResetsAt: existing?.lastWeekResetsAt ?? null,
    lastCheckedAt: nowMs,
  };

  if (!tail) {
    state.set(codeName, next);
    return;
  }

  const observation = parseUsageBanner(tail, now);
  if (!observation) {
    state.set(codeName, next);
    return;
  }

  const observedAtIso = next.lastWeekResetsAt; // alias for clarity below
  const weekResetsAtIso = observation.weekResetsAt.toISOString();
  if (
    existing &&
    existing.lastPct === observation.pct &&
    observedAtIso === weekResetsAtIso
  ) {
    state.set(codeName, next);
    return;
  }

  try {
    await api.post('/host/usage-observations', {
      agent_id: agentId,
      observed_pct: observation.pct,
      week_resets_at: weekResetsAtIso,
      source: 'pane_log',
    });
    next.lastPct = observation.pct;
    next.lastWeekResetsAt = weekResetsAtIso;
    state.set(codeName, next);
  } catch (err) {
    log(`[usage-banner] POST /host/usage-observations failed for '${codeName}': ${(err as Error).message}`);
    // Leave the in-memory state at its previous value so the next tick
    // retries the POST. lastCheckedAt is still updated so we don't hammer
    // the API at every tick on persistent failure.
    state.set(codeName, next);
  }
}
